"use client";

import { useState, useEffect } from "react";
import axios from "axios";
import Select from "react-select";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    borderColor: state.isFocused ? "#a5b4fc" : "#d1d5db",
    boxShadow: state.isFocused ? "0 0 0 1px #a5b4fc" : "none",
    "&:hover": {
      borderColor: "#a5b4fc",
    },
  }),
  input: (provided) => ({
    ...provided,
    outline: "none",
    boxShadow: "none",
    border: "none",
    margin: 0,
    padding: 0,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#e0e7ff" : "white",
    color: state.isSelected ? "#4f46e5" : "#374151",
    "&:hover": {
      backgroundColor: "#f3f4f6",
    },
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: "0.375rem",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
  }),
};

const AlertDialog = ({
  type = "error",
  title = "Alert",
  message = "Something happened.",
  confirmText = "Confirm",
  cancelText = "Cancel",
  open = false,
  onClose = () => {},
  onConfirm = () => {},
}) => {
  const styles = {
    error: {
      icon: <ExclamationTriangleIcon className="size-6 text-red-600" />,
      iconBg: "bg-red-100",
      buttonBg: "bg-red-600 hover:bg-red-500",
    },
    success: {
      icon: <CheckCircleIcon className="size-6 text-green-600" />,
      iconBg: "bg-green-100",
      buttonBg: "bg-green-600 hover:bg-green-500",
    },
  };

  const { icon, iconBg, buttonBg } = styles[type] || styles.error;

  return (
    <Dialog open={open} onClose={onClose} className="relative z-10">
      <DialogBackdrop className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in" />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95">
            <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                <span className="sr-only">Close</span>
                <XMarkIcon className="size-6" />
              </button>
            </div>
            <div className="sm:flex sm:items-start">
              <div
                className={`mx-auto flex size-12 shrink-0 items-center justify-center rounded-full ${iconBg} sm:mx-0 sm:size-10`}
              >
                {icon}
              </div>
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                <DialogTitle
                  as="h3"
                  className="text-base font-semibold text-gray-900"
                >
                  {title}
                </DialogTitle>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">{message}</p>
                </div>
              </div>
            </div>
            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                onClick={onConfirm}
                className={`inline-flex w-full justify-center rounded-md ${buttonBg} px-3 py-2 text-sm font-semibold text-white shadow-sm sm:ml-3 sm:w-auto`}
              >
                {confirmText}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
              >
                {cancelText}
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default function MaterialsView() {
  const [materials, setMaterials] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [costCodes, setCostCodes] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const userRole = localStorage.getItem("userRole");
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    unitCost: "0.00",
    container: "Sheet",
    supplier: null,
    costCode: null,
    description: "",
  });
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dialog, setDialog] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    onConfirm: () => {},
  });

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await Promise.all([fetchMaterials(), fetchVendors(), fetchCostCodes()]);
      setIsLoading(false);
    };
    loadData();
  }, []);

  const fetchMaterials = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        "https://erp-production-72da01c8e651.herokuapp.com/api/materials",
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMaterials(response.data);
    } catch (err) {
      setError("Failed to load materials.");
      console.error(err);
    }
  };

  const fetchVendors = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        "https://erp-production-72da01c8e651.herokuapp.com/api/accounts/vendors",
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVendors(response.data);
    } catch (err) {
      setError("Failed to load vendors.");
      console.error(err);
    }
  };

  const fetchCostCodes = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        "https://erp-production-72da01c8e651.herokuapp.com/api/costCodes",
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCostCodes(response.data);
    } catch (err) {
      setError("Failed to load cost codes.");
      console.error(err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "unitCost") {
      const numericValue = value.replace(/[^0-9.]/g, "");
      const parts = numericValue.split(".");
      const formattedValue =
        parts.length > 1 ? `${parts[0]}.${parts[1].slice(0, 2)}` : numericValue;
      setFormData((prev) => ({
        ...prev,
        [name]: formattedValue || "0.00",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSelectChange = (field) => (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      [field]: selectedOption ? selectedOption.value : null,
    }));
  };

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem("token");
      const submitData = {
        ...formData,
        unitCost: parseFloat(formData.unitCost) || 0,
      };
      if (editingMaterial) {
        await axios.put(
          `https://erp-production-72da01c8e651.herokuapp.com/api/materials/${editingMaterial._id}`,
          submitData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setEditingMaterial(null);
      } else {
        await axios.post(
          "https://erp-production-72da01c8e651.herokuapp.com/api/materials",
          submitData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setIsAdding(false);
      }
      setFormData({
        name: "",
        unitCost: "0.00",
        container: "Sheet",
        supplier: null,
        costCode: null,
        description: "",
      });
      fetchMaterials();
      setError(null);
    } catch (err) {
      setError("Failed to save material.");
      console.error(err);
    }
  };

  const showConfirmationDialog = (e) => {
    e.preventDefault();
    setDialog({
      open: true,
      type: "success",
      title: editingMaterial ? "Confirm Update" : "Confirm Add",
      message: editingMaterial
        ? "Are you sure you want to update this material?"
        : "Are you sure you want to add this material?",
      onConfirm: () => {
        handleSubmit();
        setDialog({ ...dialog, open: false });
      },
    });
  };

  const closeDialog = () => {
    setDialog({ ...dialog, open: false });
  };

  const editMaterial = (material) => {
    setEditingMaterial(material);
    setFormData({
      name: material.name,
      unitCost: material.unitCost.toFixed(2),
      container: material.container,
      supplier: material.supplier?._id || material.supplier || null,
      costCode: material.costCode?._id || material.costCode || null,
      description: material.description || "",
    });
    setIsAdding(false);
  };

  const vendorOptions = vendors.map((vendor) => ({
    value: vendor._id,
    label: vendor.companyName,
  }));

  const costCodeOptions = costCodes.map((costCode) => ({
    value: costCode._id,
    label: costCode.name || costCode._id,
  }));

  const containerOptions = ["Sheet", "Bag", "Box", "Tube", "Pail", "Roll"];

  const formatCost = (cost) => {
    return Number(cost).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  if (isLoading) {
    return <div className="text-center py-4">Loading...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">Materials</h1>

      <button
        onClick={() => {
          setIsAdding(true);
          setEditingMaterial(null);
          setFormData({
            name: "",
            unitCost: "0.00",
            container: "Sheet",
            supplier: null,
            costCode: null,
            description: "",
          });
        }}
        className="mb-6 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
      >
        Add Material
      </button>

      {error && <p className="text-red-600 mb-4">{error}</p>}

      {(isAdding || editingMaterial) && (
        <div className="bg-white shadow-lg rounded-lg p-6 mb-6 border border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            {editingMaterial ? "Edit Material" : "Add Material"}
          </h2>
          <form onSubmit={showConfirmationDialog} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Unit Cost ($)
              </label>
              <input
                type="text"
                name="unitCost"
                value={formData.unitCost}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Container
              </label>
              <select
                name="container"
                value={formData.container}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              >
                {containerOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Vendor
              </label>
              <Select
                options={vendorOptions}
                value={
                  vendorOptions.find(
                    (option) => option.value === formData.supplier
                  ) || null
                }
                onChange={handleSelectChange("supplier")}
                placeholder="Search for a vendor..."
                isClearable
                className="mt-1"
                classNamePrefix="select"
                styles={customStyles}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Cost Code
              </label>
              <Select
                options={costCodeOptions}
                value={
                  costCodeOptions.find(
                    (option) => option.value === formData.costCode
                  ) || null
                }
                onChange={handleSelectChange("costCode")}
                placeholder="Search for a cost code..."
                isClearable
                className="mt-1"
                classNamePrefix="select"
                styles={customStyles}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                rows="3"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => {
                  setIsAdding(false);
                  setEditingMaterial(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
              >
                Save
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white shadow-lg rounded-lg p-6 border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">
          Materials List
        </h2>
        {materials.length === 0 ? (
          <p className="text-gray-500">No materials found.</p>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[100px]">
                  Name
                </th>
                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[80px]">
                  Unit Cost
                </th>
                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[80px]">
                  Container
                </th>
                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[100px]">
                  Vendor
                </th>
                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[100px]">
                  Cost Code
                </th>
                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[150px]">
                  Description
                </th>
                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[60px]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {materials.map((material) => (
                <tr key={material._id}>
                  <td className="px-2 py-4 text-sm text-gray-900 w-[100px] truncate">
                    {material.name}
                  </td>
                  <td className="px-2 py-4 text-sm text-gray-900 w-[80px] truncate">
                    ${formatCost(material.unitCost)}
                  </td>
                  <td className="px-2 py-4 text-sm text-gray-900 w-[80px] truncate">
                    {material.container}
                  </td>
                  <td className="px-2 py-4 text-sm text-gray-900 w-[100px] truncate">
                    {material.supplier ? material.supplier.companyName : "None"}
                  </td>
                  <td className="px-2 py-4 text-sm text-gray-900 w-[100px] truncate">
                    {material.costCode
                      ? material.costCode.name || material.costCode._id
                      : "None"}
                  </td>
                  <td className="px-2 py-4 text-sm text-gray-900 w-[150px] truncate">
                    {material.description || ""}
                  </td>
                  <td className="px-2 py-4 text-sm w-[60px]">
                    <button
                      onClick={() => editMaterial(material)}
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AlertDialog
        open={dialog.open}
        type={dialog.type}
        title={dialog.title}
        message={dialog.message}
        confirmText="Confirm"
        cancelText="Cancel"
        onClose={closeDialog}
        onConfirm={dialog.onConfirm}
      />
    </div>
  );
}
